import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { AnalysisCheck, useAnalysisContext } from './contextProvider'
import { ExpandMore, HelpOutline } from '@material-ui/icons'
import { useEffect, useMemo, useState } from 'react'
import SubmitStatusModal from './submitModal'

export default function CheckSelection(): JSX.Element {
  const { analysisChecks, toggleCheck, submit, canSubmit } =
    useAnalysisContext()

  const categorizedChecks = useMemo(
    () =>
      analysisChecks.reduce(
        (acc, check) => {
          if (!acc[check.category]) {
            acc[check.category] = []
          }
          acc[check.category].push(check)
          return acc
        },
        {} as Record<string, typeof analysisChecks>,
      ),
    [analysisChecks],
  )

  const getCategoryState = (categoryChecks: AnalysisCheck[]) => {
    const selectedCount = categoryChecks.filter((check) => check.checked).length
    if (selectedCount === 0) return false
    if (selectedCount === categoryChecks.length) return true
    return 'indeterminate'
  }

  const toggleCategory = (category: string, checked: boolean) => {
    categorizedChecks[category].forEach((check) =>
      toggleCheck(check.id, checked),
    )
  }

  const [expanded, setExpanded] = useState<Record<string, boolean>>(
    Object.keys(categorizedChecks).reduce(
      (acc, category) => {
        acc[category] = true
        return acc
      },
      {} as Record<string, boolean>,
    ),
  )

  const toggleExpand = (category: string) => {
    setExpanded((prev) => ({ ...prev, [category]: !prev[category] }))
  }

  useEffect(() => {
    setExpanded((prev) =>
      Object.keys(categorizedChecks).reduce(
        (acc, category) => {
          acc[category] = prev[category] ?? true
          return acc
        },
        {} as Record<string, boolean>,
      ),
    )
  }, [categorizedChecks])
  return (
    <Box
      display='flex'
      flexDirection='column'
      flexGrow={1}
      style={{ gap: 3, margin: '16px', width: '50%' }}
    >
      <Box>
        <Typography
          variant='h5'
          style={{ lineHeight: 'normal', marginBottom: 2 }}
        >
          Fast Track Checks
        </Typography>
        <Divider style={{ marginBottom: 2 }} />
      </Box>

      <Box>
        <List>
          {Object.entries(categorizedChecks).map(([category, checks]) => (
            <Box key={category} marginBottom={2}>
              <ListItem
                style={{
                  paddingLeft: 2,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {/* Category Checkbox (Only toggles selection) */}
                <FormControlLabel
                  control={
                    <Checkbox
                      size='small'
                      checked={getCategoryState(checks) === true}
                      indeterminate={
                        getCategoryState(checks) === 'indeterminate'
                      }
                      onChange={(_, checked) =>
                        toggleCategory(category, checked)
                      }
                    />
                  }
                  label={
                    <Typography style={{ fontWeight: 'bold' }}>
                      {category}
                    </Typography>
                  }
                />
                {/* Expand/Collapse Button (Separate from Checkbox) */}
                <IconButton
                  size='small'
                  onClick={() => toggleExpand(category)}
                  style={{ marginLeft: 'auto' }}
                >
                  <ExpandMore
                    style={{
                      transform: expanded[category]
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                      transition: '0.2s',
                    }}
                  />
                </IconButton>
              </ListItem>

              {/* Expandable Section for Individual Checks */}
              <Collapse in={expanded[category]}>
                {checks.map((check) => (
                  <ListItem key={check.id} style={{ paddingLeft: 32 }}>
                    <FormControlLabel
                      control={
                        <Checkbox size='small' checked={check.checked} />
                      }
                      label={
                        <Box display='flex' alignItems='center'>
                          <Typography>{check.name}</Typography>
                          <Tooltip title={check.description} arrow>
                            <HelpOutline
                              fontSize='small'
                              style={{
                                marginLeft: 15,
                                color: 'gray',
                                cursor: 'pointer',
                              }}
                            />
                          </Tooltip>
                        </Box>
                      }
                      onChange={(_, checked: boolean) =>
                        toggleCheck(check.id, checked)
                      }
                    />
                  </ListItem>
                ))}
              </Collapse>
            </Box>
          ))}
        </List>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'left', marginTop: 4 }}>
        <Button
          variant='contained'
          color='primary'
          onClick={submit}
          disabled={!canSubmit}
        >
          Submit Analysis
        </Button>
      </Box>
      <SubmitStatusModal />
    </Box>
  )
}
